import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Wallet, Gift, Check, Loader2, Crown, Star, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BuyVotesPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [voteBalance, setVoteBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    loadData();
    
    // Check if returning from Stripe
    const sessionId = searchParams.get('session_id');
    const success = searchParams.get('success');
    
    if (sessionId && success === 'true') {
      checkPaymentStatus(sessionId);
    }
    
    const cancelled = searchParams.get('cancelled');
    if (cancelled === 'true') {
      toast.error('Payment was cancelled');
      // Clean URL
      window.history.replaceState({}, '', '/buy-votes');
    }
  }, [searchParams]);

  const loadData = async () => {
    try {
      const [packagesRes, balanceRes] = await Promise.all([
        axios.get(`${API}/vote-packages`, { withCredentials: true }),
        axios.get(`${API}/vote-balance`, { withCredentials: true })
      ]);
      
      setPackages(packagesRes.data);
      setVoteBalance(balanceRes.data.vote_balance || 0);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load vote packages');
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    setCheckingPayment(true);
    try {
      const response = await axios.get(`${API}/payment-status/${sessionId}`, { withCredentials: true });
      
      if (response.data.payment_status === 'paid') {
        toast.success(response.data.message || `🎉 ${response.data.votes_added} votes added to your balance!`);
        loadData(); // Refresh balance
      } else if (response.data.payment_status === 'unpaid') {
        toast.info('Payment is still processing...');
      }
      
      // Clean URL
      window.history.replaceState({}, '', '/buy-votes');
    } catch (error) {
      console.error('Payment status check failed:', error);
      toast.error('Failed to verify payment');
    } finally {
      setCheckingPayment(false);
    }
  };

  const handlePurchase = async (packageId) => {
    setPurchasing(packageId);
    try {
      const response = await axios.post(
        `${API}/purchase-votes`,
        { 
          package_id: packageId,
          origin_url: window.location.origin
        },
        { withCredentials: true }
      );
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Purchase failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to start purchase');
      setPurchasing(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-yellow-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-yellow-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Button
              onClick={() => navigate(-1)}
              variant="ghost"
              size="icon"
              className="hover:bg-pink-100"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 text-transparent bg-clip-text">
                🎁 Buy Votes
              </h1>
              <p className="text-sm text-gray-600">Support your favorites!</p>
            </div>
          </div>
          
          {/* Vote Balance */}
          <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-full flex items-center space-x-2">
            <Wallet className="w-5 h-5" />
            <span className="font-bold">{voteBalance} Votes</span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Checking Payment Status */}
        {checkingPayment && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center space-x-3">
            <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
            <span className="text-blue-700">Verifying your payment...</span>
          </div>
        )}

        {/* Info Card */}
        <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-2xl p-6 border border-purple-200">
          <h3 className="font-bold text-purple-800 text-lg mb-2">💝 How It Works</h3>
          <ul className="text-purple-700 space-y-2 text-sm">
            <li>• Buy vote packages below ($1 = 1 Vote)</li>
            <li>• Visit any profile in the Dating Contest</li>
            <li>• Give votes to boost their ranking! 🏆</li>
            <li>• Help your favorites win!</li>
          </ul>
        </div>

        {/* Vote Packages */}
        <div>
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <Gift className="w-6 h-6 mr-2 text-pink-500" />
            Vote Packages
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {packages.map((pkg) => (
              <div
                key={pkg.id}
                className={`relative bg-white rounded-2xl p-5 shadow-lg hover:shadow-xl transition-all border-2 ${
                  pkg.popular 
                    ? 'border-yellow-400 ring-2 ring-yellow-200' 
                    : 'border-transparent hover:border-purple-300'
                }`}
              >
                {/* Popular Badge */}
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-yellow-400 to-orange-400 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center">
                      <Crown className="w-3 h-3 mr-1" />
                      BEST VALUE
                    </span>
                  </div>
                )}
                
                {/* Icon */}
                <div className="text-center mb-3">
                  {pkg.votes >= 100 ? (
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500">
                      <Crown className="w-8 h-8 text-white" />
                    </div>
                  ) : pkg.votes >= 20 ? (
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-purple-400 to-pink-500">
                      <Star className="w-8 h-8 text-white" />
                    </div>
                  ) : (
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-pink-400 to-rose-500">
                      <Sparkles className="w-8 h-8 text-white" />
                    </div>
                  )}
                </div>
                
                {/* Votes */}
                <div className="text-center mb-3">
                  <span className="text-3xl font-bold text-gray-800">{pkg.votes}</span>
                  <p className="text-sm text-gray-500">votes</p>
                </div>
                
                {/* Price */}
                <div className="text-center mb-4">
                  <span className="text-2xl font-bold text-purple-600">${pkg.price}</span>
                </div>
                
                {/* Buy Button */}
                <Button
                  onClick={() => handlePurchase(pkg.id)}
                  disabled={purchasing !== null}
                  className={`w-full ${
                    pkg.popular
                      ? 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600'
                      : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600'
                  } text-white font-semibold`}
                >
                  {purchasing === pkg.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Buy Now'
                  )}
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Security Note */}
        <div className="bg-gray-50 rounded-xl p-4 text-center text-sm text-gray-500">
          <p>🔒 Secure payments powered by Stripe</p>
          <p className="text-xs mt-1">Your payment info is never stored on our servers</p>
        </div>
      </div>
    </div>
  );
};

export default BuyVotesPage;
